import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Sparkles, CalendarSearch, Search, Clock } from 'lucide-react';

interface SlotDiscoveryAnimationProps {
  /**
   * Final count of slots returned by GetSlots. Pass `null` while the request
   * is still in flight; the animation will hold at the "optimizing" phase
   * until the count arrives, then tick the counter up to it.
   */
  slotsFound: number | null;
  /** Fires once the counter tick + a brief hold has finished. */
  onComplete?: () => void;
}

type Phase =
  | 'intro'
  | 'scanning'
  | 'gaps'
  | 'optimizing'
  | 'revealing'
  | 'done';

const PHASE_DURATIONS: Record<Exclude<Phase, 'done'>, number> = {
  intro: 350,
  scanning: 900,
  gaps: 600,
  optimizing: 600,
  revealing: 550,
};

// Pre-baked "busy" blocks on the mini-timeline (percentages of the rail).
// Tuned so there are clear gaps between them where slot pills can land.
const BUSY_BLOCKS = [
  { start: 4, width: 14 },
  { start: 26, width: 11 },
  { start: 47, width: 13 },
  { start: 74, width: 16 },
];

// Slot pill centers (percentages). Each falls in a visible gap between
// BUSY_BLOCKS. Labels are decorative — the real count comes from the API.
const SLOT_PILLS: Array<{ x: number; label: string }> = [
  { x: 21, label: '10:00' },
  { x: 41, label: '12:15' },
  { x: 64, label: '2:30' },
  { x: 94, label: '5:00' },
];

const HOUR_TICKS = [12.5, 25, 37.5, 50, 62.5, 75, 87.5];

/**
 * Multi-phase "Slot Discovery" animation shown while the internal booking
 * flow waits on `GetSlots`. Mirrors the staged feel of
 * `BulkRouteOptimizingAnimation` but themed around scanning a day's
 * schedule, surfacing open windows, and ranking them by drive time.
 */
const SlotDiscoveryAnimation: React.FC<SlotDiscoveryAnimationProps> = ({
  slotsFound,
  onComplete,
}) => {
  const [phase, setPhase] = useState<Phase>('intro');
  const [displayedCount, setDisplayedCount] = useState(0);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  // Drive the phase machine. Phases 1-4 run on their own timers. Phase 5
  // ("revealing") only starts once `slotsFound` is known; until then we
  // hold on "optimizing" and let its visual loop continue.
  useEffect(() => {
    if (phase === 'done') return;
    if (phase === 'revealing') return;

    if (phase === 'optimizing') {
      if (slotsFound != null) {
        const t = window.setTimeout(
          () => setPhase('revealing'),
          PHASE_DURATIONS.optimizing
        );
        return () => window.clearTimeout(t);
      }
      return;
    }

    const nextByPhase: Record<'intro' | 'scanning' | 'gaps', Phase> = {
      intro: 'scanning',
      scanning: 'gaps',
      gaps: 'optimizing',
    };
    const next = nextByPhase[phase as keyof typeof nextByPhase];
    const t = window.setTimeout(() => setPhase(next), PHASE_DURATIONS[phase]);
    return () => window.clearTimeout(t);
  }, [phase, slotsFound]);

  // Counter tick-up during the reveal phase.
  useEffect(() => {
    if (phase !== 'revealing' || slotsFound == null) return;
    const target = slotsFound;
    const start = performance.now();
    let raf = 0;
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / PHASE_DURATIONS.revealing);
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplayedCount(Math.round(target * eased));
      if (t < 1) {
        raf = requestAnimationFrame(tick);
      } else {
        setDisplayedCount(target);
        const finish = window.setTimeout(() => {
          setPhase('done');
          onCompleteRef.current?.();
        }, 250);
        return () => window.clearTimeout(finish);
      }
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [phase, slotsFound]);

  const revealActive = phase === 'revealing' || phase === 'done';
  const routeActive =
    phase === 'optimizing' || phase === 'revealing' || phase === 'done';
  const pillsActive =
    phase === 'gaps' || routeActive;
  const blocksActive = phase !== 'intro' || false; // becomes true after intro
  const blocksVisible = phase !== 'intro' ? true : false;

  // Build the SVG path that connects pill centers (computed once).
  const routePath = useMemo(() => {
    const railHeight = 128; // matches the rail's pixel height below
    const pillY = railHeight - 28; // pill vertical center within rail
    const points = SLOT_PILLS.map((p) => ({ x: p.x, y: pillY }));
    // smooth-ish polyline via quadratic midpoints
    return points.reduce((acc, point, idx) => {
      if (idx === 0) return `M ${point.x} ${point.y}`;
      const prev = points[idx - 1];
      const midX = (prev.x + point.x) / 2;
      return `${acc} Q ${midX} ${point.y - 14}, ${point.x} ${point.y}`;
    }, '');
  }, []);

  const titleByPhase: Record<Phase, string> = {
    intro: "Reading your team's schedule…",
    scanning: 'Checking calendar conflicts…',
    gaps: 'Finding open time windows…',
    optimizing:
      slotsFound == null
        ? 'Optimizing by drive time…'
        : 'Ranking your best options…',
    revealing: 'All set.',
    done: 'All set.',
  };

  const subIconByPhase: Record<Phase, React.ReactNode> = {
    intro: <CalendarSearch className="h-5 w-5" />,
    scanning: <Search className="h-5 w-5" />,
    gaps: <Clock className="h-5 w-5" />,
    optimizing: <Sparkles className="h-5 w-5" />,
    revealing: <Sparkles className="h-5 w-5" />,
    done: <Sparkles className="h-5 w-5" />,
  };

  return (
    <div className="flex min-h-[420px] flex-col items-center justify-center px-6 py-10">
      {/* Phase badge */}
      <div className="mb-2 flex items-center gap-2 text-accent-600">
        <span
          className={`flex h-5 w-5 items-center justify-center ${
            phase !== 'done' ? 'animate-pulse' : ''
          }`}
        >
          {subIconByPhase[phase]}
        </span>
        <span className="text-xs font-semibold uppercase tracking-[0.18em]">
          Routemize
        </span>
      </div>

      <h3 className="mb-7 text-center text-base font-semibold text-ink-900">
        {titleByPhase[phase]}
      </h3>

      {/* Timeline rail */}
      <div className="relative h-32 w-full max-w-md overflow-hidden rounded-2xl border border-ink-200 bg-gradient-to-b from-ink-50 to-white shadow-inner-sm">
        {/* Hour grid lines */}
        {HOUR_TICKS.map((pct) => (
          <div
            key={pct}
            className="absolute top-0 bottom-0 border-l border-dashed border-ink-200/70"
            style={{ left: `${pct}%` }}
          />
        ))}

        {/* "AM / PM" rail labels */}
        <div className="pointer-events-none absolute inset-x-3 top-2 flex justify-between text-2xs font-medium uppercase tracking-wider text-ink-300">
          <span>8a</span>
          <span>12p</span>
          <span>5p</span>
        </div>

        {/* Existing busy blocks */}
        {BUSY_BLOCKS.map((b, i) => (
          <div
            key={i}
            className="absolute top-7 h-9 rounded-md border border-ink-300/60 bg-gradient-to-b from-ink-200 to-ink-300/80 shadow-sm transition-all duration-500 ease-out"
            style={{
              left: `${b.start}%`,
              width: `${b.width}%`,
              opacity: blocksVisible ? 1 : 0,
              transform: blocksVisible
                ? 'translateY(0) scale(1)'
                : 'translateY(-6px) scale(0.96)',
              transitionDelay: `${i * 70}ms`,
            }}
            aria-hidden="true"
          >
            <div className="absolute inset-x-1 top-1 h-1 rounded-sm bg-ink-400/40" />
            <div className="absolute inset-x-1 top-3 h-1 rounded-sm bg-ink-400/25" />
          </div>
        ))}

        {/* Scanner beam — animates left→right during the scanning phase */}
        <div
          className="pointer-events-none absolute top-0 bottom-0 w-[3px] rounded-full bg-gradient-to-b from-accent-400/0 via-accent-500 to-accent-400/0"
          style={{
            left: phase === 'scanning' ? '100%' : '0%',
            opacity: phase === 'scanning' ? 1 : 0,
            boxShadow:
              '0 0 10px 2px rgba(59,130,246,0.55), 0 0 20px 6px rgba(59,130,246,0.25)',
            transition: `left ${PHASE_DURATIONS.scanning}ms linear, opacity 220ms ease-out`,
          }}
          aria-hidden="true"
        />

        {/* Scanner trailing wash (subtle highlight following the beam) */}
        <div
          className="pointer-events-none absolute top-0 bottom-0 w-1/3 bg-gradient-to-r from-accent-500/0 via-accent-400/15 to-accent-400/0"
          style={{
            left: phase === 'scanning' ? '66%' : '-33%',
            opacity: phase === 'scanning' ? 1 : 0,
            transition: `left ${PHASE_DURATIONS.scanning}ms linear, opacity 220ms ease-out`,
          }}
          aria-hidden="true"
        />

        {/* Conflict checkmark dots — appear on busy blocks after scanning */}
        {BUSY_BLOCKS.map((b, i) => (
          <div
            key={`mark-${i}`}
            className="absolute top-[18px] flex h-3.5 w-3.5 items-center justify-center rounded-full bg-ink-700 text-white shadow-sm transition-all duration-300"
            style={{
              left: `calc(${b.start + b.width / 2}% - 7px)`,
              opacity:
                phase === 'gaps' || routeActive ? 1 : 0,
              transform:
                phase === 'gaps' || routeActive
                  ? 'scale(1)'
                  : 'scale(0.4)',
              transitionDelay: `${i * 50}ms`,
            }}
            aria-hidden="true"
          >
            <svg viewBox="0 0 12 12" className="h-2 w-2 stroke-white" fill="none">
              <path
                d="M2 7 L4.5 9.5 L10 3"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        ))}

        {/* Route line connecting available slot pills */}
        <svg
          className="pointer-events-none absolute inset-0 h-full w-full"
          viewBox="0 0 100 128"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <defs>
            <linearGradient id="slotRouteGradient" x1="0" x2="1">
              <stop offset="0%" stopColor="#3B82F6" />
              <stop offset="100%" stopColor="#10B981" />
            </linearGradient>
          </defs>
          <path
            d={routePath}
            fill="none"
            stroke="url(#slotRouteGradient)"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeDasharray="3 3"
            vectorEffect="non-scaling-stroke"
            style={{
              strokeDashoffset: routeActive ? 0 : 240,
              opacity: routeActive ? 1 : 0,
              transition: `stroke-dashoffset ${PHASE_DURATIONS.optimizing}ms ease-out, opacity 200ms ease-out`,
            }}
          />
        </svg>

        {/* Available slot pills — pop into the gaps */}
        {SLOT_PILLS.map((s, i) => {
          const rank = i; // 0 = best, used for accent tint
          const tintClass =
            rank === 0
              ? 'from-success-400 to-success-600 ring-success-200'
              : rank === 1
                ? 'from-accent-400 to-accent-600 ring-accent-200'
                : 'from-accent-500 to-accent-700 ring-accent-200';
          return (
            <div
              key={i}
              className={`absolute bottom-3 flex h-7 -translate-x-1/2 items-center justify-center gap-1 rounded-full bg-gradient-to-b px-2.5 text-2xs font-semibold text-white shadow-md ring-2 ring-white transition-all duration-500 ease-[cubic-bezier(0.34,1.56,0.64,1)] ${tintClass}`}
              style={{
                left: `${s.x}%`,
                opacity: pillsActive ? 1 : 0,
                transform: pillsActive
                  ? 'translate(-50%, 0) scale(1)'
                  : 'translate(-50%, 14px) scale(0.4)',
                transitionDelay: `${i * 90}ms`,
                boxShadow: routeActive
                  ? '0 4px 14px -3px rgba(16,185,129,0.45)'
                  : undefined,
              }}
            >
              <Clock className="h-2.5 w-2.5" />
              <span className="tabular-nums">{s.label}</span>
            </div>
          );
        })}

        {/* Soft pulse halo behind the top-ranked pill during optimizing/reveal */}
        <div
          className="pointer-events-none absolute bottom-2 h-9 w-9 -translate-x-1/2 rounded-full bg-success-400/40 blur-md transition-opacity duration-300"
          style={{
            left: `${SLOT_PILLS[0].x}%`,
            opacity: routeActive ? 1 : 0,
            animation: routeActive
              ? 'slotPulseHalo 1.6s ease-in-out infinite'
              : undefined,
          }}
          aria-hidden="true"
        />
      </div>

      {/* Counter / status footer */}
      <div className="mt-7 flex h-12 items-center justify-center">
        {revealActive ? (
          <div className="flex items-baseline gap-2 animate-fade-in-up">
            <span className="text-4xl font-bold text-ink-900 tabular-nums">
              {displayedCount}
            </span>
            <span className="text-sm font-medium text-ink-500">
              {displayedCount === 1 ? 'slot ready' : 'slots ready'}
            </span>
          </div>
        ) : (
          <div className="flex items-center gap-2 text-xs font-medium text-ink-500">
            <span className="inline-flex gap-1">
              <span
                className="h-1.5 w-1.5 animate-bounce rounded-full bg-accent-500"
                style={{ animationDelay: '0ms' }}
              />
              <span
                className="h-1.5 w-1.5 animate-bounce rounded-full bg-accent-500"
                style={{ animationDelay: '120ms' }}
              />
              <span
                className="h-1.5 w-1.5 animate-bounce rounded-full bg-accent-500"
                style={{ animationDelay: '240ms' }}
              />
            </span>
            <span>
              {phase === 'intro' && 'Loading day schedule'}
              {phase === 'scanning' && 'Sweeping for conflicts'}
              {phase === 'gaps' && 'Surfacing open windows'}
              {phase === 'optimizing' &&
                (slotsFound == null
                  ? 'Routemizing by drive time'
                  : 'Polishing the route')}
            </span>
          </div>
        )}
      </div>

      {/* Local keyframes — kept inline to avoid touching global tailwind config */}
      <style>{`
        @keyframes slotPulseHalo {
          0%, 100% { transform: translate(-50%, 0) scale(1); opacity: 0.55; }
          50% { transform: translate(-50%, 0) scale(1.35); opacity: 0.15; }
        }
      `}</style>
    </div>
  );
};

export default SlotDiscoveryAnimation;
